// src/pages/StudentSignup.jsx
import React, { useState } from "react";
import { auth, db, storage } from "../firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import FormInput from "../components/FormInput";
import Navbar from "../components/Navbar";

const StudentSignup = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    address: "",
    pincode: "",
    documents: null,
    scholarshipDoc: null,
  });

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  // ✅ API URL - pulled from environment (.env)
  const API_URL =
    process.env.REACT_APP_API_URL || "https://fmc-backend-c1a5.onrender.com";

  const handleChange = (e) => {
    const { name, value, files, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "file" ? (files && files[0]) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setInfo("");
    setLoading(true);

    try {
      // 1️⃣ Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;
      const idToken = await user.getIdToken();

      // Save Firebase token for future authenticated calls
      localStorage.setItem("token", idToken);

      // 2️⃣ Upload optional files
      let documentUrl = "";
      let scholarshipUrl = "";

      if (formData.documents) {
        const docRef = ref(
          storage,
          `students/${user.uid}/documents_${Date.now()}`
        );
        await uploadBytes(docRef, formData.documents);
        documentUrl = await getDownloadURL(docRef);
      }

      if (formData.scholarshipDoc) {
        const schRef = ref(
          storage,
          `students/${user.uid}/scholarship_${Date.now()}`
        );
        await uploadBytes(schRef, formData.scholarshipDoc);
        scholarshipUrl = await getDownloadURL(schRef);
      }

      // 3️⃣ Save in Firestore
      await setDoc(doc(db, "students", user.uid), {
        uid: user.uid,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        pincode: formData.pincode,
        documentUrl,
        scholarshipUrl,
        createdAt: new Date(),
      });

      // 4️⃣ Save in MongoDB via your Render backend
      const response = await fetch(`${API_URL}/api/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          password: formData.password,
          firebaseId: user.uid,
          address: formData.address,
          pincode: formData.pincode,
        }),
      });

      const result = await response.json();
      console.log("📦 Backend Response:", result);

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Failed to save in MongoDB");
      }

      // Save backend user ID
      if (result?.data?._id) {
        localStorage.setItem("userId", result.data._id);
      }

      // ✅ Success
      setInfo("🎉 Student signed up successfully!");
      setFormData({
        name: "",
        phone: "",
        email: "",
        password: "",
        address: "",
        pincode: "",
        documents: null,
        scholarshipDoc: null,
      });
    } catch (error) {
      console.error("❌ Signup Error:", error);
      setErr(error.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-[#2c301f] via-[#232736] to-[#1a1c27] flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Section */}
          <div className="hidden lg:flex relative overflow-hidden rounded-2xl bg-[rgba(53,53,36,0.85)] border border-white/10 text-white">
            <div className="relative p-10 flex flex-col justify-center">
              <h1 className="text-3xl font-semibold">
                Create your student account
              </h1>
              <p className="text-white/70 mt-2 leading-relaxed">
                Sign up to manage your applications, upload documents, and
                access personalized counselling resources.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-white/80">
                <li>✅ Secure Firebase Authentication</li>
                <li>✅ Auto-saves to MongoDB Atlas</li>
                <li>✅ Easy file upload to Cloud Storage</li>
              </ul>
            </div>
          </div>

          {/* Signup Form */}
          <div className="backdrop-blur-xl bg-[rgba(53,53,36,0.85)] border border-white/10 text-white rounded-2xl shadow-2xl p-6 sm:p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold tracking-tight">
                Student Signup
              </h2>
              <p className="text-sm text-white/70 mt-1">
                Enter your details to create an account.
              </p>
            </div>

            {/* Alerts */}
            {err && (
              <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 px-3 py-2 text-sm">
                {err}
              </div>
            )}
            {info && (
              <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 px-3 py-2 text-sm">
                {info}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name + Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput
                  label="Full Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                <FormInput
                  label="Mobile No. *"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Email + Password */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput
                  label="Email ID *"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <FormInput
                  label="Password *"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Address + Pincode */}
              <FormInput
                label="Address *"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
              />
              <FormInput
                label="Pincode *"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                required
              />

              {/* File uploads */}
              <div>
                <label className="block text-sm text-white/80 mb-1">
                  Upload Documents (optional)
                </label>
                <input
                  name="documents"
                  type="file"
                  accept=".pdf,.jpg,.png"
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm text-white/80 mb-1">
                  Scholarship Document (optional)
                </label>
                <input
                  name="scholarshipDoc"
                  type="file"
                  accept=".pdf"
                  onChange={handleChange}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2.5 rounded-lg shadow-lg shadow-yellow-900/30 transition disabled:opacity-70"
              >
                {loading ? "Submitting..." : "Create Account"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentSignup;
